"use client";

import React, { useState } from "react";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")
import { Heading } from "@/components/ui/Heading";
import Link from "next/link";
import { ArrowLeft, Loader2 } from "lucide-react";
import { formatPrice } from "@/utils/formatUtils";
import type { CartItem } from "@/core/types/cart";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { useAuth } from "@/contexts/authContext";
import { useRouter } from "next/navigation";
import { useCartStore } from "@/core/hooks/cart/useCartStore";
import { prepareCartTransaction } from "@/core/lib/api/main/cart";
import { toast } from "sonner";

interface OrderSummaryProps {
  items: CartItem[];
  currentStep: "cart" | "shipping" | "payment";
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  currentStep,
}) => {
  const { dictionary } = useDictionary();
  const {
    state: { isAuthenticated },
  } = useAuth();
  const router = useRouter();
  const [isPaying, setIsPaying] = useState(false);
  const { isSubmittingShipping, shippingProfile, cart, paymentMethod, clearCart } = useCartStore();

  const totalPriceWithoutDiscount = cart.financial_summary?.total_price_withoutDiscount ??
    items.reduce((sum, item) => sum + Number(item.product_info.price || 0) * item.quantity, 0);

  const productsDiscount = cart.financial_summary?.products_discount ??
    items.reduce((sum, item) => sum + ((Number(item.product_info.price || 0) - Number(item.product_info.final_price || 0)) * item.quantity), 0);

  const couponDiscount = cart.financial_summary?.coupon_discount ?? 0;

  const tax = cart.financial_summary?.tax ??
    Math.round((totalPriceWithoutDiscount - productsDiscount - couponDiscount) * 0.1);

  const total = cart.financial_summary?.payable_price ??
    totalPriceWithoutDiscount - productsDiscount - couponDiscount + tax;


  const handlePayment = async () => {
    if (!cart.uuid) return;
    setIsPaying(true);
    try {
      const result = await prepareCartTransaction(cart.uuid, paymentMethod);
      if (result.status === "success") {
        toast.success(result.message);

        if (result.data.payment_type === "offline") {
          router.push(`/invoices/${result.data.invoice_id}`);
          setTimeout(() => clearCart(), 100);
        } else {
          window.location.href = result.data.payment_url;
          setTimeout(() => clearCart(), 100);
        }
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error(dictionary.common.error);
    } finally {
      setIsPaying(false);
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case "shipping":
        router.push("/cart");
        break;
      case "payment":
        router.push("/shipping");
        break;
      default:
        break;
    }
  };

  const renderPrimaryButton = () => {
    switch (currentStep) {
      case "cart": {
        const href = isAuthenticated ? "/shipping" : "/login";
        return (
          <CustomButton size="full" variant="black" asChild>
            <Link href={href}>
              {isAuthenticated ? dictionary.ui.cart.continueShoppingProcess : dictionary.ui.cart.loginFirst}
            </Link>
          </CustomButton>
        );
      }

      case "shipping": {
        return (
          <CustomButton
            size="full"
            type="submit"
            form="shipping-form"
            variant="black"
            disabled={isSubmittingShipping}
          >
            {isSubmittingShipping ? (
              <Loader2 className="animate-spin" />
            ) : (
              dictionary.ui.cart.continueShoppingProcess
            )}
          </CustomButton>
        );
      }

      case "payment": {
        return (
          <CustomButton
            size="full"
            variant="black"
            onClick={handlePayment}
            disabled={isPaying}
          >
            {isPaying ? (
              <Loader2 className="animate-spin" />
            ) : (
              dictionary.ui.cart.continueShoppingProcess
            )}
          </CustomButton>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 rounded-xs p-4">
      <Heading level={2} className="mb-3">
        {dictionary.ui.cart.details}
      </Heading>

      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-primary-500">{dictionary.ui.cart.subtotal}</span>
          <span className="text-primary-700">
            {formatPrice(totalPriceWithoutDiscount)} {dictionary.ui.product.currency}
          </span>
        </div>

        {productsDiscount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-primary-500">{dictionary.ui.cart.productsDiscount}</span>
            <span className="text-red-600">
              {formatPrice(productsDiscount)} {dictionary.ui.product.currency}
            </span>
          </div>
        )}

        {couponDiscount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-primary-500">{dictionary.ui.cart.couponDiscount}</span>
            <span className="text-red-600">
              {formatPrice(couponDiscount)} {dictionary.ui.product.currency}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center">
          <span className="text-primary-500">{dictionary.ui.cart.tax}</span>
          <span className="text-primary-700">
            {formatPrice(tax)} {dictionary.ui.product.currency}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-primary-500">{dictionary.ui.cart.shipping}</span>
          <span className="text-primary-700">{dictionary.ui.cart.cashOnDelivery}</span>
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-primary-bold text-primary-900">
              {dictionary.ui.cart.total}
            </span>
            <span className="text-sm font-primary-bold text-primary-900">
              {formatPrice(total)} {dictionary.ui.product.currency}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {renderPrimaryButton()}
        {currentStep !== "cart" && (
          <CustomButton variant="outline" size="full" onClick={handleBack}>
            <ArrowLeft className="rtl:rotate-180" />
            {dictionary.common.back}
          </CustomButton>
        )}
      </div>
    </div>
  );
};